import React from "react";
import { CONFIG } from "../config";
import { ArrowRight, AlertTriangle, Timer } from "lucide-react";
import { useTimer } from "../context/TimerContext";

export default function FinalPivotSection() {
  const { formattedTime, isExpired } = useTimer();

  return (
    <section id="gain-access" className="bg-white border-t-4 md:border-t-8 border-brand-red pt-8 md:pt-10 pb-10 md:pb-16">
      <div className="max-w-[1120px] mx-auto px-4 md:px-6 text-center">

        {/* Small Warning Line */}
        <p className="flex items-center justify-center gap-2 font-display font-black uppercase text-brand-dark text-lg sm:text-xl tracking-wide mb-4" data-testid="final-close-warning-line">
          <AlertTriangle className="w-6 h-6 sm:w-7 sm:h-7 text-brand-red shrink-0" />
          <span className="bg-yellow-300 px-1.5 py-0.5 rounded">THE COLLECTION ISN&rsquo;T GOING TO REMOVE ITSELF!</span>
        </p>

        {/* Main Final Headline */}
        <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-5xl lg:text-[56px] leading-none mb-6 max-w-4xl mx-auto" data-testid="final-close-headline-1">
          <span className="text-brand-dark">THEY WANT THE FULL BALANCE.</span>{" "}
          <span className="text-brand-red">YOU DEMAND THE FULL PROOF!</span>
        </h2>

        {/* Final Pressure / Action Copy */}
        <p className="font-display font-black uppercase text-brand-dark text-base sm:text-lg md:text-xl sm:whitespace-nowrap max-w-4xl mx-auto leading-snug mb-3">
          THEY&rsquo;VE SENT YOU LETTERS, TEXTS, CALLS, LEGAL THREATS &amp; BLEMISHED YOUR CREDIT!
        </p>
        <p className="font-display font-black uppercase text-brand-dark text-base sm:text-lg md:text-xl max-w-4xl mx-auto leading-snug mb-8">
          YOU DO NOT HAVE TO STAY STUCK OR FEEL TRAPPED.
        </p>

        {/* Magically Happens Box */}
        <div className="bg-card border-2 border-brand-red rounded-lg shadow-md p-4 md:p-5 max-w-3xl mx-auto mb-8" data-testid="final-close-emphasis-box">
          <p className="font-display font-bold text-brand-dark leading-snug text-base sm:text-lg lg:text-xl">
            SEND THE <span className="text-brand-red font-black">68-DEMAND VALIDATION ATTACK.</span><br />
            FORCE THEM INTO THE <span className="underline">FEDERAL VALIDATION PROCESS</span>, IN 3 EASY STEPS.<br />
            THEN WATCH WHAT <span className="bg-yellow-300 px-1.5 py-0.5 rounded font-black">MAGICALLY HAPPENS.</span>
          </p>
        </div>

        {/* Final Decision Line */}
        <h3 className="font-display font-black uppercase text-brand-dark tracking-tight text-xl sm:text-2xl lg:text-3xl leading-snug mb-2 max-w-3xl mx-auto">
          <span className="text-brand-red">DO NOT ASSUME THAT YOU OWE</span> A THIRD
        </h3>
        <h3 className="font-display font-black uppercase text-brand-dark tracking-tight text-2xl sm:text-3xl lg:text-4xl leading-snug mb-3">
          PARTY DEBT BUYER BUSINESS <span className="text-brand-red">ANYTHING!</span>
        </h3>
        <p className="font-sans font-extrabold text-brand-dark text-base sm:text-lg md:text-xl leading-snug mb-8 sm:whitespace-nowrap" data-testid="final-close-legal-docs-line">
          Do they actually have <span className="text-brand-red font-extrabold">ANY LEGAL DOCUMENTS</span> proving this alleged debt?
        </p>

        {/* Access Timer Line */}
        <div className="flex items-center justify-center gap-2 bg-white border border-brand-border rounded-full px-4 py-1.5 mx-auto mb-5 w-fit" data-testid="final-close-timer-line">
          <Timer className="w-3.5 h-3.5 text-brand-red shrink-0" />
          <span className="font-display font-black uppercase text-brand-dark text-xs sm:text-sm tracking-wide whitespace-nowrap">
            {isExpired ? `PRICE MAY RETURN TO $${CONFIG.regularPrice} WITHOUT NOTICE` : (
              <>${CONFIG.currentPrice} ACCESS TIMER: <span className="text-brand-red">{formattedTime}</span></>
            )}
          </span>
        </div>

        {/* Final Coupon Buy Box */}
        <div
          id="final-buy-box"
          className="relative bg-card border-4 border-brand-red rounded-lg shadow-xl max-w-[700px] mx-auto mb-5"
          data-testid="final-close-buy-box"
        >
          <div className="border-2 border-dashed border-brand-red/50 rounded-md m-2 p-4 sm:p-5 md:p-6 text-center">

            <p className="font-display font-black uppercase tracking-tight text-lg sm:text-xl md:text-2xl leading-snug mb-2">
              <span className="text-brand-dark">LIMITED-TIME ONLY</span> <span className="text-brand-red">${CONFIG.currentPrice}</span>
            </p>
            <p className="font-display font-black uppercase text-brand-red text-base sm:text-lg md:text-xl leading-snug mb-2">
              START REMOVING COLLECTIONS TODAY!
            </p>
            <p className="font-display font-bold uppercase text-brand-dark text-xs sm:text-sm leading-snug mb-3">
              EVERYTHING INCLUDED VALUE: <span className="text-brand-red font-black">$2,057</span>
            </p>

            <div className="w-full h-px bg-brand-border mb-3"></div>

            <div className="flex items-center justify-center gap-2 mb-3">
              <span className="font-display font-bold uppercase text-brand-dark text-xs sm:text-sm tracking-wide">
                NORMAL PRICE:
              </span>
              <span className="font-display font-black text-brand-dark line-through decoration-brand-red decoration-[4px] text-xl sm:text-2xl leading-none">
                ${CONFIG.regularPrice}
              </span>
              <span className="bg-brand-success text-white text-xs sm:text-sm font-extrabold uppercase px-2 py-0.5 rounded tracking-wider whitespace-nowrap">
                SAVE 76%
              </span>
            </div>

            <p className="font-display font-bold uppercase text-brand-dark text-xs sm:text-sm tracking-widest mb-0.5">
              <span className="bg-yellow-300 px-1.5 py-0.5 rounded">GET INSTANT ACCESS TO THE KILL SWITCH</span>
            </p>
            <p className="font-display font-black text-brand-red text-5xl sm:text-6xl lg:text-[68px] leading-none mb-3" data-testid="final-close-today-price">
              ${CONFIG.currentPrice}
            </p>

            <a
              href={CONFIG.checkoutUrl}
              data-testid="final-close-cta-button"
              className="w-full inline-flex items-center justify-center py-4 px-8 bg-brand-cta hover:bg-brand-cta-hover text-white rounded-lg font-sans font-extrabold uppercase text-center text-sm sm:text-base tracking-wide border-b-4 border-brand-dark/30 hover:border-b-2 active:border-b-0 hover:translate-y-[2px] active:translate-y-[4px] transition-all duration-150 animate-pulse-glow hover:animate-none cursor-pointer shadow-md"
            >
              YES! I&rsquo;M READY TO REMOVE MY COLLECTIONS!
              <ArrowRight className="w-4 h-4 ml-2" />
            </a>

            <p className="text-center text-brand-dark/70 font-sans font-semibold text-xs sm:text-sm mt-3">
              🔒 <span className="font-bold">SECURE CHECKOUT</span> ⚡ <span className="font-bold">INSTANT PRIVATE ACCESS</span>
            </p>

            <p className="text-center text-brand-dark/70 font-sans font-semibold text-[11px] sm:text-xs mt-3">
              ONE PAYMENT • IMMEDIATE ACCESS • FUTURE UPDATES &amp; ADDITIONS INCLUDED
            </p>
            <p className="text-center text-brand-dark font-display font-black uppercase text-[10px] sm:text-xs tracking-wide mt-2">
              25+ CUSTOM ATTACK FILES • 68 VALIDATION DEMANDS • YOUR NEXT MOVE INCLUDED
            </p>
          </div>
        </div>

        {/* Final Page Line */}
        <h3 className="font-display font-black uppercase tracking-tight text-xl sm:text-2xl lg:text-3xl leading-snug text-brand-red">
          THE ULTIMATE DEBT COLLECTOR ATTACK SYSTEM!
        </h3>

      </div>
    </section>
  );
}
